
import AddClientAccount from './AddClientAccount.vue';
import AssignPlan from './AssignPlan.vue';
import ClientAccountDetail from './ClientAccountDetail.vue';
import { getClientAccounts } from '@/api/clientAccounts';
import { getClientLicencesUsage } from '@/api/licenses';
import { getPlanById } from '@/api/plans';

import { Component, Vue } from 'vue-property-decorator';
import Popup from '@/components/popup.vue';

export interface IPaymentPlan {
  id: number;
  name: string;
  subscription_status: string | null;
  client_account: number;
  payment_plan: number;
  billing_cycle: string | null;
  payment_option: string | null;
  subscription_current_period_end: string | null;
  provider_subscription_id: string | null;
  created: string | null;
}

export interface IEvent {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
}

@Component({
  name: 'ClientAccountsList',
  components: {
    AddClientAccount,
    AssignPlan,
    ClientAccountDetail,
    Popup
  }
})
export default class ClientAccountsList extends Vue {
  public table: any = {
    id: "client-accounts-table",
    loading: false,
    order: "id",
    columns: [
      {
        text: 'ID',
        value: 'id',
      },
      {
        text: 'Name',
        value: 'client_name',
        sortable: true
      },
      {
        text: 'Email',
        value: 'client_email',
        sortable: true
      },
      {
        text: 'Origin',
        value: 'origin',
        sortable: true
      },
      {
        text: 'Plan',
        value: 'client_payment_plans',
        sortable: true
      },
      {
        text: 'Owned Events',
        value: 'owned_events',
        sortable: true
      },
      {
        text: 'Actions',
        value: 'actions',
        sortable: false
      }
    ],
    data: [],
    dialog: false,
    assign_dialog: false,
    detail_dialog: false,
    search: "",
  };

  public selectedClient: any = {};

  public clientAccountPopup: any = {
    isActive: false,
    message: '',
    type: '',
    title: ''
  };

  public formatOrigin(origin: string): string {
    // Return 'accounts' by default because origin is always based on active plan
    // For check-in app client accounts - they always have a plan associated
    // The other client accounts type can be created without a plan associated, 
    // which means the origin will return be empty
    return origin !== '' ? origin : '-';
  }
  public formatActivePlans(plans: IPaymentPlan[]): string {
    if (!plans || !plans.length) return 'No active plans';

    let activePlans = plans.filter(plan => plan.subscription_status === "active");
    if (activePlans.length) {
      let paidPlans = activePlans.filter(plan => plan.payment_option && plan.billing_cycle);
      activePlans = paidPlans.length ? paidPlans : activePlans;
    } else {
      return 'No active plans';
    }

    return activePlans
      .map(plan => `(${plan.id}) ${plan.name}`)
      .join(', ');
  }

  public formatOwnedEvents(events: IEvent[]): string {
    return events.length.toString();
  }

  public editItem(item: any) {
    console.log('Edit item:', item);
  }

  public async viewItem(item: any) {
    // Always reset selectedClient to a new object
    this.selectedClient = {
      id: item.id,
      name: item.client_name,
    };

    let payment_plan = item.client_payment_plans.length ? item.client_payment_plans[item.client_payment_plans.length - 1] : null;
    if (payment_plan) {
      try {
        const [planResponse, licensesResponse] = await Promise.all([
          getPlanById(payment_plan.payment_plan),
          getClientLicencesUsage(item.id)
        ]);
        // Replace the object reference to trigger reactivity
        this.selectedClient = {
          id: item.id,
          name: item.client_name,
          email: item.client_email,
          payment_plan,
          plan_limits: planResponse.data,
          licenses_usage: licensesResponse.data,
        };
        this.table.detail_dialog = true;
      } catch (error) {
        console.error('Error fetching plan details or client licenses:', error);
        // Still open dialog with partial info if you want
        this.table.detail_dialog = true;
      }
    } else {
      // No plan, open dialog with minimal info
      this.table.detail_dialog = true;
    }
  }

  public associateItem(item: any) {
    this.selectedClient = {
      id: item.id,
      name: item.client_name,
    };
    this.table.assign_dialog = true;
  }

  public loadClientAccounts = () => {
    this.table.loading = true;
    getClientAccounts()
      .then((response: any) => {
        this.table.data = response.data.results;
      })
      .catch((error) => {
        console.error("Error loading client accounts:", error);
      })
      .finally(() => {
        this.table.loading = false;
      });
  };

  public handleClientAccountCreated = () => {
    this.table.dialog = false;
    this.showPopup('Client created successfully', 'success');
    this.loadClientAccounts();
  };

  public handlePlanAssignment = () => {
    this.table.assign_dialog = false;
    this.showPopup('Plan assigned to client successfully', 'success');
    this.loadClientAccounts();
  };

  public closeDetailDialog() {
    this.table.detail_dialog = false;
    this.selectedClient = {};
  }


  public showPopup(message: string, type: string = 'info') {
    this.clientAccountPopup.isActive = true;
    this.clientAccountPopup.message = message;
    this.clientAccountPopup.type = type;

    setTimeout(() => {
      this.clientAccountPopup.isActive = false;
      this.clientAccountPopup.message = "";
      this.clientAccountPopup.type = "";
    }, 5000);
  }

  mounted() {
    this.loadClientAccounts();
  }
}
